<template>
  <div class="quick-video-page">
    <header class="quick-header">
      <h1 class="quick-title">视频快速创作</h1>
      <!--<p class="quick-sub">独立视频生成，无需工作流；生成逻辑与分镜步骤四一致（九宫格、参考图、异步任务）。</p>-->
    </header>

    <div ref="feedScroll" class="quick-feed" @scroll="onFeedScroll">
      <div v-if="loadingMore" class="feed-hint">加载中…</div>
      <div v-if="!jobs.length && !listLoading" class="feed-hint">暂无记录，在下方输入描述并生成。</div>
      <article v-for="job in jobsChronological" :key="'j-' + job.taskId" class="feed-card">
        <div class="feed-card-head">
          <span>{{ formatModelLabel(job.modelCode) }} · {{ job.durationSeconds }}s · {{ job.resolution }}</span>
          <span class="feed-time">{{ formatTime(job.createTime) }}</span>
        </div>
        <div class="feed-prompt" v-html="renderFeedPromptHtml(job)"></div>
        <div v-if="job.status === 'pending' || job.status === 'running'" class="feed-status ok">
          {{ job.message || '生成中…' }}<span v-if="job.progress">（{{ job.progress }}%）</span>
        </div>
        <div v-else-if="job.status === 'failed'" class="feed-status err">
          {{ job.errorMessage || '生成失败' }}
          <button type="button" class="link" @click="retryFromJob(job)">重新编辑</button>
        </div>
        <div v-else-if="job.videoUrl" class="feed-video-box">
          <video class="feed-video" :src="mediaUrl(job.videoUrl)" controls playsinline />
          <div class="feed-actions">
            <button type="button" class="btn" @click="retryFromJob(job)">重新编辑</button>
            <button type="button" class="btn" @click="regenerateFromJob(job)">再次生成</button>
          </div>
        </div>
      </article>
    </div>

    <div class="composer">
      <div class="ref-strip">
        <button type="button" class="ref-add" @click="uploadLocal">+ 参考</button>
        <button type="button" class="ref-add" @click="openAssetModal">资产库</button>
        <div v-for="a in taskAssetLibrary" :key="a.type + '-' + a.id" class="ref-wrap">
          <button type="button" class="ref-x" @click.stop="removeStripAsset(a)">×</button>
          <div class="ref-thumb" :style="{ backgroundImage: 'url(' + a.cover + ')' }" />
        </div>
        <input ref="localUploadInput" type="file" accept="image/*" hidden @change="onLocalUploadChange" />
      </div>
      <div class="prompt-wrap">
        <div
          ref="promptEditor"
          class="prompt-editor"
          contenteditable="true"
          role="textbox"
          data-placeholder="输入镜头描述；@ 引用参考素材"
          @input="onPromptInput"
          @keydown="onPromptKeydown"
          @blur="onPromptBlur"
        ></div>
        <div v-if="mentionVisible" class="mention-panel">
          <button
            v-for="item in mentionOptions"
            :key="item.type + '-' + item.id"
            type="button"
            class="mention-item"
            @mousedown.prevent="insertMention(item)"
          >
            <span class="mention-item-main">
              <img
                v-if="item.cover && !isPlaceholderAsset(item.cover)"
                class="mention-thumb"
                :src="mediaUrl(item.cover)"
                :alt="item.name"
              />
              <span v-else class="mention-thumb mention-thumb--empty placeholder-block" />
              <span class="mention-name">@{{ item.name }}</span>
            </span>
            <span class="mention-type">{{ mentionTypeLabel(item.type) }}</span>
          </button>
          <div v-if="!mentionOptions.length" class="mention-empty">暂无可引用素材，请先点击「资产库」或「+ 参考」选择/上传</div>
        </div>
      </div>
      <div class="toolbar">
        <select v-model="selectedModel" class="tb-sel">
          <option v-for="o in modelOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
        </select>
        <select v-model="selectedDuration" class="tb-sel">
          <option v-for="d in durationOptions" :key="d" :value="d">{{ d }}</option>
        </select>
        <select v-model="selectedResolution" class="tb-sel" >
          <option v-for="r in resolutionOptions" :key="r" :value="r">{{ r }}</option>
        </select>
        <button type="button" class="tb-at" @click="insertAtSymbol" v-if="false">@</button>
        <button type="button" class="tb-send" :disabled="submitting || !videoPointsPerSecond" @click="handleSubmit">
          {{ submitting ? '生成中…' : '发送 · ' + generatePointsDisplay + ' 积分' }}
        </button>
      </div>
    </div>

    <div v-if="assetModalVisible" class="mask" @click.self="closeAssetModal">
      <div class="modal">
        <h3>资产库</h3>
        <div class="tabs">
          <button
            v-for="t in assetTypes"
            :key="t.value"
            type="button"
            :class="{ on: activeAssetType === t.value }"
            @click="switchAssetModalTab(t.value)"
          >{{ t.label }}</button>
        </div>
        <div class="grid">
          <div
            v-for="item in filteredModalAssets"
            :key="item.id"
            class="grid-item"
            :class="{ on: isTempAssetSelected(item.id) }"
            @click="toggleTempAsset(item.id)"
          >
            <img :src="item.cover" :alt="item.name" />
            <span>{{ item.name }}</span>
          </div>
        </div>
        <div class="modal-ft">
          <button type="button" @click="closeAssetModal">取消</button>
          <button type="button" class="primary" @click="confirmAssets">确认</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userApi } from "../api/user";

const DEF_DUR = ["4s", "5s", "6s", "7s", "8s", "9s", "10s", "11s", "12s", "13s", "14s", "15s"];
const DEF_RES = ["480p", "720p", "1080p"];

export default {
  name: "VideoQuickCreatePage",
  data() {
    return {
      jobs: [],
      listLoading: false,
      loadingMore: false,
      page: 1,
      hasMore: true,
      submitting: false,
      pollTimers: {},
      selectedModel: "",
      selectedDuration: "15s",
      selectedResolution: "720p",
      videoModelsRaw: [],
      assetLibrary: [],
      taskAssetLibrary: [],
      assetModalVisible: false,
      activeAssetType: "role",
      assetTypes: [
        { value: "role", label: "角色" },
        { value: "scene", label: "场景" },
        { value: "material", label: "素材" }
      ],
      tempSelectedAssetIds: [],
      modalAssetSelectionDraft: { role: [], scene: [], material: [] },
      mentionVisible: false,
      mentionQuery: ""
    };
  },
  computed: {
    jobsChronological() {
      return [...this.jobs].sort((a, b) => Number(a.taskId) - Number(b.taskId));
    },
    modelOptions() {
      return (this.videoModelsRaw || []).map(m => ({
        value: String(m.code || ""),
        label: String(m.name || m.code || "")
      }));
    },
    currentVideoModel() {
      return (this.videoModelsRaw || []).find(m => m.code === this.selectedModel) || null;
    },
    durationOptions() {
      const m = this.currentVideoModel;
      return m && m.durations && m.durations.length ? m.durations : DEF_DUR;
    },
    resolutionOptions() {
      const m = this.currentVideoModel;
      return m && m.resolutions && m.resolutions.length ? m.resolutions : DEF_RES;
    },
    /** 宽表：提交 references、@ 名称匹配（不含未勾选的资产库全量） */
    mentionSourceLibrary() {
      const map = new Map();
      const add = x => {
        if (!x || !x.name) return;
        const t = this.normType(x.type);
        map.set(`${t}:${x.name}`, { ...x, type: t });
      };
      this.taskAssetLibrary.forEach(add);
      return [...map.values()];
    },
    /** @ 下拉候选：仅参考条（资产库勾选 / + 参考上传） */
    mentionPickerLibrary() {
      return this.mentionSourceLibrary;
    },
    mentionOptions() {
      const kw = this.mentionQuery.toLowerCase();
      const source = this.mentionPickerLibrary;
      if (!kw) return source.slice(0, 8);
      const matched = source.filter(x => String(x.name).toLowerCase().includes(kw));
      if (!matched.length) return source.slice(0, 8);
      return matched.slice(0, 8);
    },
    mentionAssetByNameFirst() {
      const m = new Map();
      this.taskAssetLibrary.forEach(it => {
        const n = String(it.name || "").trim();
        if (!n || m.has(n)) return;
        m.set(n, { ...it, type: this.normType(it.type) });
      });
      return m;
    },
    filteredModalAssets() {
      return this.assetLibrary.filter(x => x.type === this.activeAssetType);
    },
    videoPointsPerSecond() {
      const m = this.currentVideoModel;
      const res = this.selectedResolution;
      const p = m && m.pricing && m.pricing[res];
      return p && p.pointsPerSecond > 0 ? p.pointsPerSecond : 0;
    },
    generatePointsDisplay() {
      const sec = this.parseDurationSeconds(this.selectedDuration);
      const rate = this.videoPointsPerSecond;
      if (!rate || !sec) return "—";
      return String(Math.max(1, Math.round(rate * sec)));
    }
  },
  watch: {
    selectedModel() {
      this.$nextTick(() => this.syncDurRes());
    }
  },
  async mounted() {
    await Promise.all([this.loadModels(), this.loadAssets()]);
    await this.loadJobs(true);
    this.$nextTick(() => this.scrollBottom());
  },
  beforeDestroy() {
    Object.values(this.pollTimers).forEach(t => clearInterval(t));
  },
  methods: {
    mediaUrl(s) {
      s = String(s || "").trim();
      if (!s) return "";
      return /^https?:\/\//i.test(s) ? s : (s.startsWith("/") ? s : `/${s}`);
    },
    isPlaceholderAsset(src) {
      return /dummyimage\.com/i.test(String(src || ""));
    },
    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(Number(ts) * 1000);
      return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    },
    formatModelLabel(code) {
      const r = (this.videoModelsRaw || []).find(m => m.code === code);
      return r ? (r.name || r.code) : code || "—";
    },
    parseDurationSeconds(v) {
      const m = String(v).match(/^(\d+)\s*s$/i);
      return m ? Math.max(4, parseInt(m[1], 10)) : 15;
    },
    normType(t) {
      t = String(t || "").toLowerCase();
      return t === "role" || t === "scene" ? t : "material";
    },
    mentionTypeLabel(t) {
      return t === "role" ? "角色" : t === "scene" ? "场景" : "素材";
    },
    async loadModels() {
      try {
        this.videoModelsRaw = await userApi.videoModels() || [];
      } catch (e) {
        this.videoModelsRaw = [];
      }
      if (this.videoModelsRaw.length && !this.selectedModel) {
        this.selectedModel = this.videoModelsRaw[0].code;
      }
      this.syncDurRes();
    },
    syncDurRes() {
      if (this.durationOptions.indexOf(this.selectedDuration) < 0) {
        this.selectedDuration = this.durationOptions[this.durationOptions.length - 1];
      }
      if (this.resolutionOptions.indexOf(this.selectedResolution) < 0) {
        this.selectedResolution = this.resolutionOptions[0];
      }
    },
    async loadAssets() {
      try {
        const data = await userApi.assets();
        this.assetLibrary = ((data && data.image) || []).map(it => ({
          id: it.id,
          type: it.assetType || "material",
          name: it.name || "",
          cover: this.mediaUrl(it.cover || it.url)
        }));
      } catch (e) {
        this.assetLibrary = [];
      }
    },
    async loadJobs(reset) {
      if (reset) {
        this.page = 1;
        this.hasMore = true;
        this.jobs = [];
        this.listLoading = true;
      } else if (!this.hasMore || this.loadingMore) return;
      else this.loadingMore = true;
      try {
        const res = await userApi.listQuickVideoJobs({ page: this.page, pageSize: 20 });
        const items = (res && res.items) || [];
        this.hasMore = !!(res && res.hasMore);
        const map = new Map(this.jobs.map(j => [j.taskId, j]));
        items.forEach(it => map.set(Number(it.taskId), { ...it }));
        this.jobs = [...map.values()].sort((a, b) => b.taskId - a.taskId);
        items.forEach(it => {
          if (it.status === "pending" || it.status === "running") this.startPoll(Number(it.taskId));
        });
        if (this.hasMore) this.page += 1;
      } finally {
        this.listLoading = false;
        this.loadingMore = false;
      }
    },
    onFeedScroll() {
      const el = this.$refs.feedScroll;
      if (el && el.scrollTop < 60) this.loadJobs(false);
    },
    scrollBottom() {
      const el = this.$refs.feedScroll;
      if (el) el.scrollTop = el.scrollHeight;
    },
    upsertJob(patch) {
      const id = Number(patch.taskId);
      if (!id) return;
      const i = this.jobs.findIndex(j => j.taskId === id);
      if (i < 0) this.jobs.unshift({ ...patch });
      else this.$set(this.jobs, i, { ...this.jobs[i], ...patch });
    },
    startPoll(taskId) {
      if (!taskId || this.pollTimers[taskId]) return;
      const tick = async () => {
        try {
          const st = await userApi.getQuickVideoTaskStatus({ taskId });
          const prev = this.jobs.find(j => Number(j.taskId) === taskId);
          this.upsertJob({
            taskId,
            status: st.status,
            progress: st.progress,
            message: st.message,
            errorMessage: st.errorMessage,
            prompt: st.prompt,
            references: (st.references && st.references.length) ? st.references : (prev && prev.references) || [],
            modelCode: st.modelCode,
            resolution: st.resolution,
            durationSeconds: st.durationSeconds,
            videoUrl: (st.result && st.result.videoUrl) || ""
          });
          if (st.status === "success" || st.status === "failed") {
            clearInterval(this.pollTimers[taskId]);
            delete this.pollTimers[taskId];
          }
        } catch (e) { /* noop */ }
      };
      tick();
      this.pollTimers[taskId] = setInterval(tick, 10000);
    },
    buildRefs(text) {
      const names = [];
      const seen = new Set();
      String(text).replace(/@([^\s@]+)/g, (_, n) => {
        n = n.trim();
        if (n && !/^Image\d+$/i.test(n) && !seen.has(n)) {
          seen.add(n);
          names.push(n);
        }
        return "";
      });
      const byName = new Map(this.mentionSourceLibrary.map(x => [x.name, x]));
      const w = { role: 0.9, scene: 0.7, material: 0.6 };
      const out = [];
      const sk = new Set();
      const push = (hit, name) => {
        const key = `${hit.type}:${name}`;
        if (sk.has(key) || !hit.cover) return;
        sk.add(key);
        out.push({
          type: "image",
          url: hit.cover,
          weight: w[hit.type] || 0.7,
          bindType: hit.type,
          bindName: name
        });
      };
      names.forEach(n => {
        const hit = byName.get(n);
        if (hit) push(hit, n);
      });
      this.taskAssetLibrary.forEach(it => push(it, it.name));
      return out.slice(0, 9);
    },
    toBackendPrompt(display, refs) {
      const map = new Map();
      refs.forEach((r, i) => {
        if (r.bindName) map.set(r.bindName, i + 1);
      });
      const body = String(display).replace(/@([^\s@]+)/g, (full, name) => {
        const i = map.get(String(name).trim());
        return i ? `@Image${i}` : full;
      });
      return body.replace(/^\s*生成语言\s*[：:][^\n]*\n?/gm, "").trim();
    },
    /** 去掉 @提及 与正文之间因 contenteditable/innerText 产生的多余换行 */
    normalizePromptLineBreaks(text) {
      return String(text || "")
        .replace(/(@[^\s@]+)[ \t]*\r?\n[ \t]*/g, "$1 ")
        .replace(/\r?\n[ \t]*(?=@)/g, " ");
    },
    serializePromptEditor(editor) {
      const parts = [];
      const walk = node => {
        if (!node) return;
        if (node.nodeType === Node.TEXT_NODE) {
          parts.push(node.nodeValue || "");
          return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        if (node.classList && node.classList.contains("mention-tag")) {
          const label = String(node.getAttribute("data-label") || "").trim();
          if (label) {
            parts.push(`@${label}`);
            return;
          }
          const textEl = node.querySelector(".mention-tag-text");
          const raw = textEl ? String(textEl.textContent || "") : "";
          parts.push(raw.startsWith("@") ? raw : `@${raw.replace(/^@/, "")}`);
          return;
        }
        Array.from(node.childNodes || []).forEach(walk);
      };
      walk(editor);
      return parts.join("");
    },
    getPromptText() {
      const ed = this.$refs.promptEditor;
      if (!ed) return "";
      const raw = ed.querySelector(".mention-tag")
        ? this.serializePromptEditor(ed)
        : String(ed.innerText || "");
      return this.normalizePromptLineBreaks(raw.replace(/\u00A0/g, " ")).trim();
    },
    escapeHtml(s) {
      return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    /** 将落库的 @Image1… 按 references 顺序还原为 @实体名（与步骤四一致） */
    expandAtImagePlaceholders(prompt, references) {
      const s = String(prompt || "");
      if (!/@Image\d+/i.test(s)) return s;
      const names = (references || [])
        .map(r => String((r && r.bindName) || "").trim())
        .filter(n => n && !/^Image\d+$/i.test(n));
      if (!names.length) return s;
      return s.replace(/@Image(\d+)/gi, (_, num) => {
        const i = parseInt(num, 10) - 1;
        return i >= 0 && i < names.length ? `@${names[i]}` : `@Image${num}`;
      });
    },
    buildMentionMapFromReferences(references) {
      const m = new Map();
      (references || []).forEach((r, idx) => {
        const name = String((r && r.bindName) || "").trim();
        if (!name || /^Image\d+$/i.test(name)) return;
        const cover = this.mediaUrl((r && (r.url || r.cover)) || "");
        if (!m.has(name)) {
          m.set(name, {
            id: `ref-${idx}`,
            name,
            type: this.normType(r && r.bindType),
            cover
          });
        }
      });
      return m;
    },
    renderFeedPromptHtml(job) {
      const text = this.normalizePromptLineBreaks(
        this.expandAtImagePlaceholders(
          (job && job.prompt) || "",
          (job && job.references) || []
        )
      );
      const byName = this.buildMentionMapFromReferences(job && job.references);
      this.mentionAssetByNameFirst.forEach((it, name) => {
        if (!byName.has(name)) byName.set(name, it);
      });
      const sortedNames = [...byName.keys()].sort((a, b) => b.length - a.length);
      let html = "";
      let i = 0;
      while (i < text.length) {
        const c = text[i];
        if (c !== "@" && c !== "\uFF20") {
          const nextAt = (() => {
            for (let j = i + 1; j < text.length; j++) {
              if (text[j] === "@" || text[j] === "\uFF20") return j;
            }
            return text.length;
          })();
          html += this.escapeHtml(text.slice(i, nextAt));
          i = nextAt;
          continue;
        }
        const rest = text.slice(i + 1);
        let hit = null;
        for (let si = 0; si < sortedNames.length; si++) {
          const name = sortedNames[si];
          if (rest.startsWith(name)) {
            hit = name;
            break;
          }
        }
        if (hit && byName.has(hit)) {
          html += this.mentionTagHtml(byName.get(hit));
          i += 1 + hit.length;
        } else {
          html += this.escapeHtml(c);
          i += 1;
        }
      }
      return html || "&nbsp;";
    },
    mentionTagHtml(item) {
      const name = this.escapeHtml(item.name || "");
      const cover = item.cover ? this.escapeHtml(item.cover) : "";
      let inner = `<span class="mention-tag-text">@${name}</span>`;
      if (cover) {
        inner = `<img class="mention-tag-img" src="${cover}" alt="" draggable="false" />${inner}`;
      }
      return `<span class="mention-tag">${inner}</span>`;
    },
    getTextBeforeCaret(editor, selection) {
      const range = selection.getRangeAt(0).cloneRange();
      range.selectNodeContents(editor);
      range.setEnd(selection.anchorNode, selection.anchorOffset);
      return range.toString();
    },
    onPromptInput() {
      const ed = this.$refs.promptEditor;
      const sel = window.getSelection();
      if (!ed) {
        this.mentionVisible = false;
        return;
      }
      let textBefore = this.getPromptText();
      if (sel && sel.rangeCount > 0) {
        textBefore = this.getTextBeforeCaret(ed, sel);
      }
      const m = String(textBefore || "").replace(/\uFF20/g, "@").match(/@\s*([^\s@]*)$/);
      if (!m) {
        this.mentionVisible = false;
        this.mentionQuery = "";
        return;
      }
      this.mentionQuery = String(m[1] || "").trim();
      this.mentionVisible = true;
    },
    onPromptKeydown(e) {
      if (e.key === "@") {
        this.$nextTick(() => {
          this.mentionQuery = "";
          this.mentionVisible = true;
        });
      }
      if (!this.mentionVisible) return;
      if (e.key === "Enter" && this.mentionOptions.length) {
        e.preventDefault();
        this.insertMention(this.mentionOptions[0]);
      }
      if (e.key === "Escape") {
        this.mentionVisible = false;
      }
    },
    onPromptBlur() {
      setTimeout(() => {
        this.mentionVisible = false;
        this.autoTagMentionsInEditor();
      }, 120);
    },
    insertAtSymbol() {
      const ed = this.$refs.promptEditor;
      if (ed) {
        ed.focus();
        document.execCommand("insertText", false, "@");
        this.onPromptInput();
      }
    },
    createMentionNode(item) {
      const mention = document.createElement("span");
      mention.className = "mention-tag";
      mention.setAttribute("contenteditable", "false");
      mention.setAttribute("data-id", String(item.id || ""));
      mention.setAttribute("data-type", item.type || "material");
      mention.setAttribute("data-label", item.name || "");
      if (item.cover) {
        const img = document.createElement("img");
        img.className = "mention-tag-img";
        img.src = item.cover;
        img.alt = item.name || "";
        img.setAttribute("draggable", "false");
        mention.appendChild(img);
      }
      const text = document.createElement("span");
      text.className = "mention-tag-text";
      text.textContent = `@${item.name}`;
      mention.appendChild(text);
      return mention;
    },
    removeMentionTriggerBeforeCaret(selection) {
      const anchor = selection.anchorNode;
      const offset = selection.anchorOffset;
      if (!anchor || anchor.nodeType !== Node.TEXT_NODE) return;
      const text = anchor.textContent || "";
      const prefix = text.slice(0, offset);
      const suffix = text.slice(offset);
      const match = prefix.replace(/\uFF20/g, "@").match(/@\s*([^\s@]*)$/);
      if (!match) return;
      const start = prefix.length - match[0].length;
      anchor.textContent = `${prefix.slice(0, start)}${suffix}`;
      const range = document.createRange();
      range.setStart(anchor, start);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    },
    insertMention(item) {
      const ed = this.$refs.promptEditor;
      const sel = window.getSelection();
      if (!ed || !sel || !sel.rangeCount) return;
      this.removeMentionTriggerBeforeCaret(sel);
      const range = sel.getRangeAt(0);
      const mention = this.createMentionNode(item);
      const trailingSpace = document.createTextNode(" ");
      range.deleteContents();
      range.insertNode(trailingSpace);
      range.insertNode(mention);
      range.setStartAfter(trailingSpace);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      this.mentionVisible = false;
      this.mentionQuery = "";
      ed.focus();
    },
    autoTagMentionsInEditor() {
      const editor = this.$refs.promptEditor;
      if (!editor) return;
      const byName = this.mentionAssetByNameFirst;
      if (!byName || !byName.size) return;
      const sortedNames = [...byName.keys()].sort((a, b) => b.length - a.length);
      const isInsideMention = node => {
        let p = node && node.parentNode;
        while (p && p !== editor) {
          if (p.nodeType === Node.ELEMENT_NODE && p.classList && p.classList.contains("mention-tag")) return true;
          p = p.parentNode;
        }
        return false;
      };
      const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
        acceptNode: node => {
          if (!node || !node.nodeValue || isInsideMention(node)) return NodeFilter.FILTER_REJECT;
          const nv = String(node.nodeValue || "");
          if (nv.indexOf("@") === -1 && nv.indexOf("\uFF20") === -1) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        }
      });
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);
      textNodes.forEach(node => {
        const text = String(node.nodeValue || "");
        const frag = document.createDocumentFragment();
        let changed = false;
        let i = 0;
        while (i < text.length) {
          const c = text[i];
          if (c !== "@" && c !== "\uFF20") {
            let nextAt = text.length;
            for (let j = i + 1; j < text.length; j++) {
              if (text[j] === "@" || text[j] === "\uFF20") {
                nextAt = j;
                break;
              }
            }
            frag.appendChild(document.createTextNode(text.slice(i, nextAt)));
            i = nextAt;
            continue;
          }
          const rest = text.slice(i + 1);
          let hitName = null;
          for (let si = 0; si < sortedNames.length; si++) {
            if (rest.startsWith(sortedNames[si])) {
              hitName = sortedNames[si];
              break;
            }
          }
          if (hitName && byName.has(hitName)) {
            frag.appendChild(this.createMentionNode(byName.get(hitName)));
            frag.appendChild(document.createTextNode(" "));
            i += 1 + hitName.length;
            changed = true;
          } else {
            frag.appendChild(document.createTextNode(c));
            i += 1;
          }
        }
        if (changed) {
          node.parentNode.replaceChild(frag, node);
        }
      });
    },
    openAssetModal() {
      const draft = { role: [], scene: [], material: [] };
      this.taskAssetLibrary.forEach(it => {
        const t = this.normType(it.type);
        if (!draft[t].includes(it.id)) draft[t].push(it.id);
      });
      this.modalAssetSelectionDraft = draft;
      this.tempSelectedAssetIds = [...draft[this.activeAssetType]];
      this.assetModalVisible = true;
    },
    switchAssetModalTab(t) {
      this.modalAssetSelectionDraft[this.activeAssetType] = [...this.tempSelectedAssetIds];
      this.activeAssetType = t;
      this.tempSelectedAssetIds = [...(this.modalAssetSelectionDraft[t] || [])];
    },
    closeAssetModal() {
      this.assetModalVisible = false;
    },
    isTempAssetSelected(id) {
      return this.tempSelectedAssetIds.some(x => String(x) === String(id));
    },
    toggleTempAsset(id) {
      const s = String(id);
      const list = this.tempSelectedAssetIds;
      const i = list.findIndex(x => String(x) === s);
      this.tempSelectedAssetIds = i < 0 ? [...list, id] : list.filter(x => String(x) !== s);
    },
    confirmAssets() {
      const cur = this.activeAssetType;
      const draft = { ...this.modalAssetSelectionDraft, [cur]: [...this.tempSelectedAssetIds] };
      const byKey = new Map(this.assetLibrary.map(x => [`${x.type}:${x.id}`, x]));
      const next = [];
      ["role", "scene", "material"].forEach(t => {
        (draft[t] || []).forEach(id => {
          const it = byKey.get(`${t}:${id}`);
          if (it) next.push({ ...it, type: t });
        });
      });
      this.taskAssetLibrary = next;
      this.assetModalVisible = false;
    },
    removeStripAsset(a) {
      this.taskAssetLibrary = this.taskAssetLibrary.filter(x => !(x.type === a.type && x.id === a.id));
    },
    uploadLocal() {
      const inp = this.$refs.localUploadInput;
      if (inp) inp.click();
    },
    async onLocalUploadChange(ev) {
      const file = ev.target && ev.target.files && ev.target.files[0];
      if (!file) return;
      try {
        const r = await userApi.uploadAsset({ file, assetType: this.activeAssetType, name: file.name });
        const item = {
          id: r.id || `local-${Date.now()}`,
          type: this.activeAssetType,
          name: r.name || file.name,
          cover: this.mediaUrl(r.cover || r.url)
        };
        if (!this.taskAssetLibrary.some(x => x.type === item.type && x.name === item.name)) {
          this.taskAssetLibrary.push(item);
        }
        if (!this.assetLibrary.some(x => String(x.id) === String(item.id))) {
          this.assetLibrary.push(item);
        }
      } catch (e) {
        window.alert((e && e.message) || "上传失败");
      }
      ev.target.value = "";
    },
    retryFromJob(job) {
      const ed = this.$refs.promptEditor;
      const display = this.expandAtImagePlaceholders(job.prompt || "", job.references || []);
      if (ed) {
        ed.innerText = display;
        this.$nextTick(() => this.autoTagMentionsInEditor());
      }
      const refs = job.references || [];
      if (refs.length) {
        const byKey = new Map(this.assetLibrary.map(x => [`${this.normType(x.type)}:${String(x.name).trim()}`, x]));
        const strip = [];
        refs.forEach((r, idx) => {
          const name = String((r && r.bindName) || "").trim();
          if (!name) return;
          const t = this.normType(r && r.bindType);
          const hit = byKey.get(`${t}:${name}`);
          strip.push(
            hit || {
              id: `ref-${idx}`,
              type: t,
              name,
              cover: this.mediaUrl((r && r.url) || "")
            }
          );
        });
        if (strip.length) this.taskAssetLibrary = strip;
      }
      if (job.modelCode) this.selectedModel = job.modelCode;
      if (job.resolution) this.selectedResolution = job.resolution;
      if (job.durationSeconds) this.selectedDuration = `${job.durationSeconds}s`;
    },
    regenerateFromJob(job) {
      this.retryFromJob(job);
      this.$nextTick(() => this.handleSubmit());
    },
    async handleSubmit() {
      if (this.submitting) return;
      const display = this.getPromptText();
      if (!display) {
        window.alert("请先输入镜头描述");
        return;
      }
      if (!this.videoPointsPerSecond) {
        window.alert("当前模型未配置扣费");
        return;
      }
      const refs = this.buildRefs(display);
      const prompt = this.toBackendPrompt(display, refs);
      const durationSeconds = this.parseDurationSeconds(this.selectedDuration);
      this.submitting = true;
      try {
        const res = await userApi.createQuickVideoTask({
          modelCode: this.selectedModel,
          resolution: this.selectedResolution,
          durationSeconds,
          prompt,
          references: JSON.stringify(refs),
          aspectRatio: "16:9"
        });
        const taskId = Number(res.taskId);
        if (!taskId) throw new Error("创建失败");
        this.upsertJob({
          taskId,
          status: "pending",
          progress: 0,
          message: "等待生成",
          prompt,
          references: refs,
          modelCode: this.selectedModel,
          resolution: this.selectedResolution,
          durationSeconds
        });
        this.startPoll(taskId);
        this.$nextTick(() => this.scrollBottom());
      } catch (e) {
        window.alert((e && e.message) || "提交失败");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.quick-video-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 20px);
  background: #333333;
}
.quick-header {
  padding: 14px 18px 6px;
  flex-shrink: 0;
}
.quick-title {
  margin: 0;
  font-size: 20px;
}
.quick-sub {
  margin: 4px 0 0;
  font-size: 13px;
  color: #6b7280;
}
.quick-feed {
  flex: 1;
  overflow-y: auto;
  padding: 8px 16px 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.feed-hint {
  text-align: center;
  color: #9ca3af;
  font-size: 13px;
}
.feed-card {
  background: #1f1f1f;
  border-radius: 12px;
  padding: 12px 14px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}
.feed-card-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 6px;
}
.feed-time {
  flex-shrink: 0;
  color: #9ca3af;
}
.feed-prompt {
  margin: 0;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
}
.feed-prompt ::v-deep .mention-tag {
  display: inline-flex;
  align-items: center;
  margin: 0 2px;
  padding: 1px 6px 1px 2px;
  border-radius: 999px;
  background: #19b06b;
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  vertical-align: middle;
}
.feed-prompt ::v-deep .mention-tag-img {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  object-fit: cover;
  margin-right: 4px;
}
.feed-prompt ::v-deep .mention-tag-text {
  display: inline-block;
}
.prompt-editor ::v-deep .mention-tag {
  display: inline-flex;
  align-items: center;
  margin: 0 2px;
  padding: 1px 6px 1px 2px;
  border-radius: 999px;
  background: #19b06b;
  color: #fff;
  font-size: 12px;
  line-height: 16px;
  white-space: nowrap;
  vertical-align: middle;
}
.prompt-editor ::v-deep .mention-tag-img {
  width: 16px;
  height: 16px;
  border-radius: 999px;
  object-fit: cover;
  margin-right: 4px;
}
.prompt-editor ::v-deep .mention-tag-text {
  display: inline-block;
}
.feed-status.ok {
  color: #19b06b;
  font-size: 13px;
  margin-top: 8px;
}
.feed-status.err {
  color: #a1a1a1;
  font-size: 13px;
  margin-top: 8px;
}
.link {
  border: none;
  background: none;
  color: #19b06b;
  cursor: pointer;
  margin-left: 6px;
}
.feed-video {
  width: 100%;
  max-height: 360px;
  border-radius: 8px;
  margin-top: 8px;
  background: #000;
}
.feed-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}
.btn {
  border: 1px solid #e5e7eb;
  background: #1f1f1f;
  border-radius: 8px;
  padding: 4px 10px;
  cursor: pointer;
  font-size: 13px;
}
.composer {
  flex-shrink: 0;
  background: #333333;
  border-top: 1px solid #000000;
  padding: 10px 14px 14px;
}
.ref-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}
.ref-add,
.ref-wrap {
  position: relative;
}
.ref-add {
  width: 52px;
  height: 52px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #333333;
  cursor: pointer;
  font-size: 12px;
  color:#fff;
}
.ref-thumb {
  width: 52px;
  height: 52px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  background-color: #e5e7eb;
}
.ref-x {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: #111;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}
.prompt-wrap {
  position: relative;
}
.prompt-editor {
  min-height: 80px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 14px;
  outline: none;
  background: #333333;
}
.prompt-editor:empty:before {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
}
.mention-panel {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 100%;
  margin-bottom: 4px;
  background: #111;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-height: 180px;
  overflow-y: auto;
  z-index: 5;
  
}
.mention-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  background: none;
  padding: 8px 10px;
  cursor: pointer;
  color: #fff;
}
.mention-item:hover {
  background: #333333;
}
.mention-item-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.mention-thumb {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  object-fit: cover;
}
.mention-thumb--empty {
  display: inline-block;
  background: #3a3a3a;
}
.mention-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.mention-type {
  flex-shrink: 0;
  color: #9ca3af;
  font-size: 12px;
}
.mention-empty {
  padding: 10px 12px;
  color: #9ca3af;
  font-size: 12px;
}
.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-top: 8px;
}
.tb-sel {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 0 8px;
  font-size: 13px;
  background:#1f1f1f;
  color:#ffffff;
}
.tb-at {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  background: #1f1f1f;
  cursor: pointer;
  color:#ffffff;
}
.tb-send {
  margin-left: auto;
  height: 34px;
  padding: 0 16px;
  border: none;
  border-radius: 17px;
  background: linear-gradient(135deg, #19b06b, #19b06b);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
}
.tb-send:disabled {
  opacity: 0.5;
}
.mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal {
  background: #333;
  border-radius: 12px;
  padding: 16px;
  width: min(600px, 92vw);
  max-height: 80vh;
  overflow: auto;
}
.tabs {
  display: flex;
  gap: 8px;
  margin: 10px 0;
}
.tabs button {
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: #000;
  cursor: pointer;
  color:#fff;
}
.tabs button.on {
  border-color: #19b06b;
}
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
  gap: 8px;
}
.grid-item {
  min-width: 0;
  border: 2px solid transparent;
  border-radius: 8px;
  padding: 4px;
  text-align: center;
  font-size: 12px;
  cursor: pointer;
}
.grid-item.on {
  border-color: #19b06b;
}
.grid-item img {
  width: 100%;
  height: 68px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}
.grid-item span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.modal-ft {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.modal-ft .primary {
  background: #4f46e5;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
}
</style>
